// src/components/Sprints/SprintBoard.tsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Button,
  Typography,
  Menu,
  IconButton,
  Tooltip,
  alpha,
  Container,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  TextField,
  Avatar,
} from '@mui/material';
import {
  Add,
  CalendarToday,
  ExpandMore,
  Notifications,
  Person,
  Lock,
  Public,
  Menu as MenuIcon,
  Link as LinkIcon,
  Chat as ChatIcon,
  Settings as SettingsIcon,
  IntegrationInstructions as IntegrateIcon,
  Assistant as SidekickIcon,
  Star,
  StarBorder,
  InsertEmoticon,
  ArticleOutlined,
  Close as CloseIcon,
  WorkspacesOutlined,
  DashboardOutlined,
} from '@mui/icons-material';
import { useParams, useSearchParams } from 'react-router-dom';
import TableBoardView from '../../components/Board/TableBoardView';
import FilterPanel, { FilterOptions } from '../../components/Board/FilterPanel';
import ProgressDashboard from '../../components/Dashboard/ProgressDashboard';
import SprintManagementView from '../SprintManagement/SprintManagementView';
import CreateTaskModal from '../../components/Tasks/CreateTaskModal';
import AddMemberModal from '../../components/Workspace/AddMemberModal';
import EpicManagement from '../../components/Epics/EpicManagement';
import ActivityFeed from '../../components/Activity/ActivityFeed';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { usePermissions } from '../../hooks/usePermissions';
import { fetchTasks, updateTask, patchTaskLocal } from '../../store/taskSlice';
import { fetchEpics } from '../../store/epicSlice';
import { fetchSprints } from '../../store/sprintSlice';
import { fetchBoards, fetchBoard } from '../../store/boardSlice';
import { fetchWorkspace } from '../../store/workspaceSlice';
import { showToast } from '../../store/uiSlice';
import { Task, TaskStatus, TaskPriority, TaskType } from '../../types/common';
import './SprintBoard.scss';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index} className="sprint-board__tab-panel">
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

// Custom hook for outside click detection
const useOutsideClick = (callback: () => void) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        callback();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [callback]);

  return ref;
};

const SprintBoard: React.FC = () => {
  const dispatch = useAppDispatch();
  const { boardId: paramBoardId } = useParams<{ boardId: string }>();
  const { currentBoard, boards, loading: boardsLoading } = useAppSelector((state) => state.boards);
  const { currentWorkspace } = useAppSelector((state) => state.workspaces);
  const effectiveBoardId = paramBoardId || currentBoard?.id;
  // Was hardcoded to '1' - AddMemberModal below uses this to invite people
  // into "the workspace", so a wrong/placeholder id here means invites
  // could silently target the wrong workspace. Resolve it from the actual
  // board instead, same as BoardPage does.
  const effectiveWorkspaceId = currentBoard?.workspaceId || currentWorkspace?.id || '';
  const { canCreateTask } = usePermissions(effectiveWorkspaceId);
  const [selectedBoardId, setSelectedBoardId] = useState('');

  // No board resolved from the URL or Redux (e.g. arriving here straight from
  // the sidebar link, which carries no board id) — load the user's boards so
  // they can pick one instead of silently guessing a board id.
  useEffect(() => {
    if (!effectiveBoardId) {
      dispatch(fetchBoards());
    } else if (effectiveBoardId && currentBoard?.id !== effectiveBoardId) {
      dispatch(fetchBoard(effectiveBoardId));
    }
  }, [dispatch, effectiveBoardId, currentBoard?.id]);

  useEffect(() => {
    if (currentBoard?.workspaceId && currentWorkspace?.id !== currentBoard.workspaceId) {
      dispatch(fetchWorkspace(currentBoard.workspaceId));
    }
  }, [currentBoard?.workspaceId, currentWorkspace?.id, dispatch]);

  const { tasks, loading: tasksLoading } = useAppSelector((state) => state.tasks);
  const { sprints, activeSprint } = useAppSelector((state) => state.sprints);
  const { epics } = useAppSelector((state) => state.epics);

  const [currentTab, setCurrentTab] = useState(0);
  const [openTaskDialog, setOpenTaskDialog] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [newTaskStatus, setNewTaskStatus] = useState<TaskStatus>(TaskStatus.TODO);
  const [groupBy, setGroupBy] = useState<'sprint' | 'epic' | 'status' | 'none'>('sprint');
  const [sortBy, setSortBy] = useState('createdAt-desc');
  const [searchParams, setSearchParams] = useSearchParams();

  // Deliberately only seeds the initial `filters` state below (via
  // useState(initialFiltersFromUrl)) - it must not recompute on every
  // searchParams change, or user-driven filter edits would keep getting
  // clobbered back to whatever the URL said on first render.
  const initialFiltersFromUrl = useMemo<FilterOptions>(() => {
    const statusParam = searchParams.get('status');
    const assigneeParam = searchParams.get('assignee') || searchParams.get('assigneeId');
    const priorityParam = searchParams.get('priority');
    const typeParam = searchParams.get('type');
    const searchVal = searchParams.get('search') || '';

    return {
      status: statusParam ? (statusParam.split(',') as TaskStatus[]) : [],
      priority: priorityParam ? (priorityParam.split(',') as TaskPriority[]) : [],
      type: typeParam ? (typeParam.split(',') as TaskType[]) : [],
      assignees: assigneeParam ? assigneeParam.split(',') : [],
      epics: [],
      sprints: [],
      search: searchVal,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [filters, setFilters] = useState<FilterOptions>(initialFiltersFromUrl);

  const handleFilterChange = (newFilters: FilterOptions) => {
    setFilters(newFilters);
    const newParams = new URLSearchParams(searchParams);

    if (newFilters.status.length > 0) {
      newParams.set('status', newFilters.status.join(','));
    } else {
      newParams.delete('status');
    }

    if (newFilters.assignees.length > 0) {
      newParams.set('assignee', newFilters.assignees.join(','));
    } else {
      newParams.delete('assignee');
      newParams.delete('assigneeId');
    }

    if (newFilters.priority.length > 0) {
      newParams.set('priority', newFilters.priority.join(','));
    } else {
      newParams.delete('priority');
    }

    if (newFilters.type.length > 0) {
      newParams.set('type', newFilters.type.join(','));
    } else {
      newParams.delete('type');
    }

    if (newFilters.search) {
      newParams.set('search', newFilters.search);
    } else {
      newParams.delete('search');
    }

    setSearchParams(newParams, { replace: true });
  };

  // Sprint dropdown states
  const [sprintTitle, setSprintTitle] = useState('Sprints');
  const [sprintDescription, setSprintDescription] = useState('Add description...');
  const [editingSprintTitle, setEditingSprintTitle] = useState('Sprints');
  const [editingSprintDescription, setEditingSprintDescription] = useState('Add description...');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  
  // Sprint dropdown menu state
  const [sprintMenuAnchorEl, setSprintMenuAnchorEl] = useState<null | HTMLElement>(null);
  
  // Board type (rendered via MUI Select, which manages its own open state)
  const [boardType, setBoardType] = useState('main');

  // Refs with custom outside click hooks
  const emojiPickerRef = useOutsideClick(() => setShowEmojiPicker(false));

  const titleInputRef = useRef<HTMLInputElement>(null);
  const descInputRef = useRef<HTMLTextAreaElement>(null);

  // Emoji data
  const emojiList = ['🚀', '⭐', '🔥', '💎', '🎯', '⚡', '🛠️', '📱', '🔧', '🌟'];

  // Board type options
  const boardTypeOptions = [
    { value: 'main', label: 'Main', icon: <Person sx={{ fontSize: 16 }} /> },
    { value: 'private', label: 'Private', icon: <Lock sx={{ fontSize: 16 }} /> },
    { value: 'public', label: 'Public', icon: <Public sx={{ fontSize: 16 }} /> },
  ];

  useEffect(() => {
    if (effectiveBoardId) {
      dispatch(fetchEpics(effectiveBoardId));
    }
  }, [dispatch, effectiveBoardId]);

  useEffect(() => {
    if (effectiveBoardId) {
      dispatch(fetchSprints(effectiveBoardId));
    }
  }, [dispatch, effectiveBoardId]);

  useEffect(() => {
    const apiFilters: any = {};
    if (filters.status.length > 0) {
      apiFilters.status = filters.status.join(',');
    }
    if (filters.assignees.length > 0) {
      apiFilters.assigneeId = filters.assignees[0];
      apiFilters.assignee = filters.assignees[0];
    }
    if (effectiveBoardId) {
      dispatch(fetchTasks({ boardId: effectiveBoardId, filters: apiFilters }));
    }
  }, [dispatch, effectiveBoardId, filters.status, filters.assignees]);

  // Focus management
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  useEffect(() => {
    if (isEditingDescription && descInputRef.current) {
      descInputRef.current.focus();
      // Auto-expand height when editing
      descInputRef.current.style.height = 'auto';
      descInputRef.current.style.height = descInputRef.current.scrollHeight + 'px';
    }
  }, [isEditingDescription]);

  // Sprint dropdown menu handlers
  const handleSprintMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setSprintMenuAnchorEl(event.currentTarget);
  };

  const handleSprintMenuClose = () => {
    setSprintMenuAnchorEl(null);
  };

  // Sprint title functions
  const addEmojiToTitle = (emoji: string) => {
    setEditingSprintTitle(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  const handleTitleFocus = () => {
    setEditingSprintTitle(sprintTitle);
    setIsEditingTitle(true);
  };

  const handleTitleBlur = () => {
    if (editingSprintTitle.trim()) {
      setSprintTitle(editingSprintTitle);
    }
    setIsEditingTitle(false);
  };

  const handleTitleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (editingSprintTitle.trim()) {
        setSprintTitle(editingSprintTitle);
      }
      setIsEditingTitle(false);
    } else if (e.key === 'Escape') {
      setEditingSprintTitle(sprintTitle);
      setIsEditingTitle(false);
    }
  };

  // Description functions - UPDATED for auto height
  const handleDescriptionFocus = () => {
    setEditingSprintDescription(sprintDescription);
    setIsEditingDescription(true);
    setIsDescriptionExpanded(true);
    
    // Auto adjust height
    setTimeout(() => {
      if (descInputRef.current) {
        descInputRef.current.style.height = 'auto';
        descInputRef.current.style.height = descInputRef.current.scrollHeight + 'px';
      }
    }, 0);
  };

  const handleDescriptionBlur = () => {
    setSprintDescription(editingSprintDescription);
    setIsEditingDescription(false);
    setIsDescriptionExpanded(false);
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditingSprintDescription(e.target.value);
    
    // Auto adjust height
    if (descInputRef.current) {
      descInputRef.current.style.height = 'auto';
      descInputRef.current.style.height = descInputRef.current.scrollHeight + 'px';
    }
  };

  const handleDescriptionKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      setSprintDescription(editingSprintDescription);
      setIsEditingDescription(false);
    } else if (e.key === 'Escape') {
      setEditingSprintDescription(sprintDescription);
      setIsEditingDescription(false);
    }
  };

  // Board type functions
  const handleBoardTypeSelect = (type: string) => {
    setBoardType(type);
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleOpenTaskDialog = (status: TaskStatus) => {
    setNewTaskStatus(status);
    setOpenTaskDialog(true);
  };

  const handleCloseTaskDialog = () => {
    setOpenTaskDialog(false);
  };

  // Returns whether the update succeeded, so callers that need to react
  // differently to success vs. failure (e.g. TaskDetail's assignee picker,
  // which shows its own success toast) don't have to guess - failure is
  // already reported here via toast, so callers only need to handle success.
  const handleUpdateTask = async (taskId: string, updates: Partial<Task>): Promise<boolean> => {
    // Apply immediately (e.g. dragging a card to another column) so the UI
    // never waits on the network, then reconcile with the server response.
    const previousTask = tasks.find((task) => task.id === taskId);
    dispatch(patchTaskLocal({ taskId, updates }));

    try {
      await dispatch(updateTask({ taskId, data: updates })).unwrap();
      return true;
    } catch (error: any) {
      if (previousTask) {
        dispatch(patchTaskLocal({ taskId, updates: previousTask }));
      }
      // updateTask's thunk rejects with a plain string (via rejectWithValue)
      // carrying the backend's actual validation message, not an Error
      // object - fall back to .message only for the rare non-string case.
      dispatch(
        showToast({
          message: (typeof error === 'string' ? error : error?.message) || 'Failed to update task',
          type: 'error',
        })
      );
      return false;
    }
  };

  // Filter and sort tasks
  const filteredAndSortedTasks = useMemo(() => {
    let filtered = [...tasks];

    if (filters.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(
        (task) =>
          task.title.toLowerCase().includes(search) ||
          task.description?.toLowerCase().includes(search) ||
          task.id.toLowerCase().includes(search)
      );
    }

    if (filters.status.length > 0) {
      filtered = filtered.filter((task) => filters.status.includes(task.status));
    }

    if (filters.priority.length > 0) {
      filtered = filtered.filter((task) => task.priority && filters.priority.includes(task.priority));
    }

    if (filters.type.length > 0) {
      filtered = filtered.filter((task) => task.type && filters.type.includes(task.type));
    }

    if (filters.epics.length > 0) {
      filtered = filtered.filter((task) => task.epicId && filters.epics.includes(task.epicId));
    }

    if (filters.sprints.length > 0) {
      filtered = filtered.filter((task) => task.sprintId && filters.sprints.includes(task.sprintId));
    }

    const [sortField, sortOrder] = sortBy.split('-');
    filtered.sort((a, b) => {
      let aVal: any = a[sortField as keyof Task];
      let bVal: any = b[sortField as keyof Task];

      if (sortField === 'title') {
        aVal = aVal?.toLowerCase() || '';
        bVal = bVal?.toLowerCase() || '';
      }

      if (aVal === undefined || aVal === null) return 1;
      if (bVal === undefined || bVal === null) return -1;

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [tasks, filters, sortBy]);

  if (!effectiveBoardId) {
    return (
      <Container maxWidth="xs" sx={{ py: 8 }}>
        <Paper sx={{ p: 4, borderRadius: 3 }} elevation={2}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
            Select a board
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Choose which board's sprints you'd like to view.
          </Typography>

          {boardsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={28} />
            </Box>
          ) : boards.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              You don't have any boards yet. Create one from your workspace first.
            </Typography>
          ) : (
            <>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Board</InputLabel>
                <Select
                  value={selectedBoardId}
                  label="Board"
                  onChange={(e) => setSelectedBoardId(e.target.value)}
                >
                  {boards.map((board) => (
                    <MenuItem key={board.id} value={board.id}>
                      {board.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                fullWidth
                variant="contained"
                size="large"
                disabled={!selectedBoardId}
                onClick={() => dispatch(fetchBoard(selectedBoardId))}
              >
                View Sprint Board
              </Button>
            </>
          )}
        </Paper>
      </Container>
    );
  }

  return (
    <Box className="sprint-board">
      {/* Main Header Section */}
      <Box className="sprint-board__main-header">
        {/* Board Indicator - this page is reachable via a board-agnostic
            URL (Favorites shortcut), so which board is in scope must always
            be visible and switchable here, not just inferred silently. */}
        {boards.length > 1 && (
          <FormControl size="small" sx={{ minWidth: 160, mr: 1.5 }}>
            <Select
              value={effectiveBoardId || ''}
              onChange={(e) => dispatch(fetchBoard(e.target.value as string))}
              sx={{
                fontWeight: 600,
                fontSize: '0.9rem',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha('#000', 0.12) },
              }}
            >
              {boards.map((board) => (
                <MenuItem key={board.id} value={board.id}>
                  {board.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Sprint Dropdown Section */}
        <Box className="sprint-board__sprint-section">
          {/* Workspace & Board Context Badges */}
          {(currentWorkspace || currentBoard) && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 0.75,
                flexWrap: 'wrap',
              }}
            >
              {currentWorkspace && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: '#6366f1',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    bgcolor: 'rgba(99, 102, 241, 0.08)',
                    px: 1,
                    py: 0.25,
                    borderRadius: 1.5,
                  }}
                >
                  <WorkspacesOutlined sx={{ fontSize: 14 }} />
                  <span>{currentWorkspace.name}</span>
                </Box>
              )}

              {currentBoard && (
                <>
                  {currentWorkspace && (
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>
                      /
                    </Typography>
                  )}
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: '#0f172a',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      bgcolor: 'rgba(15, 23, 42, 0.05)',
                      px: 1,
                      py: 0.25,
                      borderRadius: 1.5,
                    }}
                  >
                    <DashboardOutlined sx={{ fontSize: 14, color: '#6366f1' }} />
                    <span>{currentBoard.name}</span>
                  </Box>
                </>
              )}
            </Box>
          )}

          <Tooltip title="Sprint Options">
            <Box
              className="sprint-dropdown-trigger"
              onClick={handleSprintMenuOpen}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                padding: '8px 12px',
                borderRadius: 2,
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: alpha('#000', 0.04),
                },
              }}
            >
              <Typography variant="h6" className="sprint-title" sx={{ fontWeight: 600, color: '#323338' }}>
                {sprintTitle}
              </Typography>
              <ExpandMore 
                sx={{ 
                  color: '#676879',
                  transition: 'transform 0.2s',
                  transform: sprintMenuAnchorEl ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
            </Box>
          </Tooltip>

          {/* Sprint Dropdown Menu */}
          <Menu
            anchorEl={sprintMenuAnchorEl}
            open={Boolean(sprintMenuAnchorEl)}
            onClose={handleSprintMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            sx={{
              mt: 1,
              '& .MuiPaper-root': {
                borderRadius: 2,
                width: 320,
                maxWidth: '90vw',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                border: '1px solid',
                borderColor: 'divider',
              },
            }}
          >
            {/* Sprint Header Section */}
            <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TextField
                  inputRef={titleInputRef}
                  variant="standard"
                  fullWidth
                  value={isEditingTitle ? editingSprintTitle : sprintTitle}
                  onChange={(e) => isEditingTitle && setEditingSprintTitle(e.target.value)}
                  onFocus={handleTitleFocus}
                  onBlur={handleTitleBlur}
                  onKeyPress={handleTitleKeyPress}
                  placeholder="Sprint name"
                  InputProps={{
                    readOnly: !isEditingTitle,
                    disableUnderline: !isEditingTitle,
                    sx: { fontSize: '1.05rem', fontWeight: 700, color: '#323338' },
                  }}
                />
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                  {isEditingTitle && (
                    <Tooltip title="Add emoji">
                      <IconButton
                        size="small"
                        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                        sx={{
                          width: 32,
                          height: 32,
                          color: '#6b7280',
                          '&:hover': {
                            bgcolor: alpha('#0073ea', 0.08),
                            color: '#0073ea',
                          },
                        }}
                      >
                        <InsertEmoticon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}>
                    <IconButton
                      size="small"
                      onClick={() => setIsFavorite(!isFavorite)}
                      sx={{
                        width: 32,
                        height: 32,
                        color: isFavorite ? '#ffc107' : '#c0c0c0',
                        '&:hover': {
                          bgcolor: alpha('#ffc107', 0.1),
                          color: '#ffb300',
                        },
                      }}
                    >
                      {isFavorite ? <Star fontSize="small" /> : <StarBorder fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Description Section */}
              <TextField
                inputRef={descInputRef}
                variant="outlined"
                fullWidth
                multiline
                minRows={1}
                maxRows={isDescriptionExpanded ? 8 : 3}
                value={isEditingDescription ? editingSprintDescription : sprintDescription}
                onChange={handleDescriptionChange}
                onFocus={handleDescriptionFocus}
                onBlur={handleDescriptionBlur}
                onKeyDown={handleDescriptionKeyPress}
                placeholder="Add a description for this sprint..."
                InputProps={{ readOnly: !isEditingDescription }}
                sx={{
                  mt: 1.5,
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.85rem',
                    borderRadius: 1.5,
                    bgcolor: alpha('#000', 0.02),
                  },
                }}
              />
            </Box>

            {/* Board Info Section */}
            <Box sx={{ px: 2.5, py: 2 }}>
              <Typography
                variant="caption"
                sx={{ color: '#9297a5', fontWeight: 700, letterSpacing: 0.6, mb: 1.5, display: 'block' }}
              >
                BOARD INFO
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
                {/* Board Type with Dropdown */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#676879' }}>
                    <ArticleOutlined sx={{ fontSize: 18 }} />
                    <Typography variant="body2">Board type</Typography>
                  </Box>
                  <FormControl size="small" variant="standard" sx={{ minWidth: 110 }}>
                    <Select
                      value={boardType}
                      onChange={(e) => handleBoardTypeSelect(e.target.value)}
                      disableUnderline
                      IconComponent={ExpandMore}
                      renderValue={(value) => (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          {boardTypeOptions.find((opt) => opt.value === value)?.icon}
                          <Typography variant="body2" fontWeight={600}>
                            {boardTypeOptions.find((opt) => opt.value === value)?.label}
                          </Typography>
                        </Box>
                      )}
                      sx={{
                        fontSize: '0.875rem',
                        '& .MuiSelect-select': { display: 'flex', alignItems: 'center', py: 0.5 },
                      }}
                    >
                      {boardTypeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {option.icon}
                            {option.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Owner */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#676879' }}>
                    <Person sx={{ fontSize: 18 }} />
                    <Typography variant="body2">Owner</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Avatar sx={{ width: 22, height: 22, fontSize: 12, bgcolor: '#4A90E2' }}>A</Avatar>
                    <Typography variant="body2" fontWeight={600}>
                      Ahsin Siddique
                    </Typography>
                  </Box>
                </Box>

                {/* Created by */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#676879' }}>
                    <CalendarToday sx={{ fontSize: 18 }} />
                    <Typography variant="body2">Created</Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={600}>
                    Oct 11, 2025
                  </Typography>
                </Box>

                {/* Notifications */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#676879' }}>
                    <Notifications sx={{ fontSize: 18 }} />
                    <Typography variant="body2">Notifications</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography variant="body2" fontWeight={600}>
                      Everything
                    </Typography>
                    <Box
                      sx={{
                        px: 0.75,
                        py: 0.1,
                        borderRadius: 1,
                        bgcolor: alpha('#4A90E2', 0.12),
                        color: '#4A90E2',
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      New
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Menu>

          {/* Emoji Picker */}
          {showEmojiPicker && (
            <Box
              ref={emojiPickerRef}
              sx={{
                position: 'absolute',
                top: '60px',
                right: '12px',
                background: 'white',
                border: '1px solid #e6e9ef',
                borderRadius: 2,
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                zIndex: 1001,
                width: 220,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 1.5,
                  py: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#676879' }}>
                  Choose Emoji
                </Typography>
                <IconButton size="small" onClick={() => setShowEmojiPicker(false)}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, p: 1.25 }}>
                {emojiList.map((emoji, index) => (
                  <IconButton
                    key={index}
                    size="small"
                    onClick={() => addEmojiToTitle(emoji)}
                    sx={{
                      fontSize: '1.2rem',
                      borderRadius: 1.5,
                      '&:hover': {
                        bgcolor: alpha('#0073ea', 0.08),
                      },
                    }}
                  >
                    {emoji}
                  </IconButton>
                ))}
              </Box>
            </Box>
          )}
        </Box>

        {/* Header Actions Section */}
        <Box className="sprint-board__header-actions">
          {/* Sidekick Icon */}
          <Tooltip title="Sidekick">
            <IconButton 
              size="small" 
              className="header-action-btn"
              sx={{ 
                color: '#676879',
                '&:hover': { backgroundColor: alpha('#000', 0.04) }
              }}
            >
              <SidekickIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          {/* Integrate Icon */}
          <Tooltip title="Integrate">
            <IconButton 
              size="small" 
              className="header-action-btn"
              sx={{ 
                color: '#676879',
                '&:hover': { backgroundColor: alpha('#000', 0.04) }
              }}
            >
              <IntegrateIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          {/* Automation Icon */}
          <Tooltip title="Automation">
            <IconButton 
              size="small" 
              className="header-action-btn"
              sx={{ 
                color: '#676879',
                '&:hover': { backgroundColor: alpha('#000', 0.04) }
              }}
            >
              <SettingsIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>

          {/* Message Icon */}
          <Tooltip title="Messages">
            <IconButton 
              size="small" 
              className="header-action-btn"
              sx={{ 
                color: '#676879',
                '&:hover': { backgroundColor: alpha('#000', 0.04) }
              }}
            >
              <ChatIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
           {/* Profile Icon */}
          <Tooltip title="Workspace Members / Invite">
            <IconButton 
              size="small" 
              className="header-action-btn"
              onClick={() => setInviteModalOpen(true)}
              sx={{ 
                color: '#676879',
                '&:hover': { backgroundColor: alpha('#000', 0.04) }
              }}
            >
              <Person sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          {/* Invite Link Icon */}
          <Tooltip title="Invite Team Members">
            <IconButton 
              size="small" 
              className="header-action-btn"
              onClick={() => setInviteModalOpen(true)}
              sx={{ 
                color: '#676879',
                '&:hover': { backgroundColor: alpha('#000', 0.04) }
              }}
            >
              <LinkIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          {/* Three Lines Menu Icon */}
          <Tooltip title="Menu">
            <IconButton 
              size="small" 
              className="header-action-btn"
              sx={{ 
                color: '#676879',
                '&:hover': { backgroundColor: alpha('#000', 0.04) }
              }}
            >
              <MenuIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          
        </Box>
      </Box>

      {/* Tabs Section */}
      <Box className="sprint-board__header">
        <Tabs value={currentTab} onChange={handleTabChange} className="sprint-board__tabs">
          <Tab label="Table" />
          <Tab label="Progress" />
          <Tab label="Sprints" />
          <Tab label="Epics" />
          <Tab label="Activity" />
        </Tabs>

        {canCreateTask && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenTaskDialog(TaskStatus.TODO)}
            className="sprint-board__add-task-btn"
          >
            New Task
          </Button>
        )}
      </Box>

      {/* Filter Panel */}
      {currentTab === 0 && (
        <FilterPanel
          filters={filters}
          onFilterChange={handleFilterChange}
          sortBy={sortBy}
          onSortChange={setSortBy}
          groupBy={groupBy}
          onGroupByChange={setGroupBy}
          availableEpics={epics.map((e) => ({ id: e.id, name: e.name }))}
          availableSprints={sprints.map((s) => ({ id: s.id, name: s.name }))}
        />
      )}

      {/* Content Area */}
      <Box className="sprint-board__content">
        <TabPanel value={currentTab} index={0}>
          <TableBoardView
            tasks={filteredAndSortedTasks}
            sprints={sprints}
            epics={epics}
            onUpdateTask={handleUpdateTask}
            onCreateTask={() => handleOpenTaskDialog(TaskStatus.TODO)}
            groupBy={groupBy}
            canCreateTask={canCreateTask}
            boardId={effectiveBoardId}
          />
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          <ProgressDashboard tasks={filteredAndSortedTasks} activeSprint={activeSprint} />
        </TabPanel>

        <TabPanel value={currentTab} index={2}>
          <SprintManagementView boardId={effectiveBoardId} />
        </TabPanel>

        <TabPanel value={currentTab} index={3}>
          <EpicManagement boardId={effectiveBoardId} />
        </TabPanel>

        <TabPanel value={currentTab} index={4}>
          <ActivityFeed boardId={effectiveBoardId} workspaceId={effectiveWorkspaceId} limit={50} showRecent={false} />
        </TabPanel>
      </Box>

      {/* Create Task Dialog */}
      <CreateTaskModal
        open={openTaskDialog}
        onClose={handleCloseTaskDialog}
        boardId={effectiveBoardId}
        defaultStatus={newTaskStatus}
        defaultSprintId={activeSprint?.id}
        epics={epics.map((e) => ({ id: e.id, name: e.name }))}
      />
      {/* Add Member Dialog */}
      <AddMemberModal
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        workspaceId={effectiveWorkspaceId}
      />
    </Box>
  );
};

export default SprintBoard;